import { useState, useEffect, useRef } from "react";
import { GeocodedAddressData } from '@/shared/components/AddressAutocomplete';
import { STEP_QUESTIONS } from './MatchmakingStepperModal/StepQuestions';
import { QuestionStep } from './MatchmakingStepperModal/QuestionStep';
import { LocationStep } from './MatchmakingStepperModal/LocationStep';
import { NavigationButtons } from './MatchmakingStepperModal/NavigationButtons';

/**
 * Feature 060: Removed service range and radius related props
 * Simplified to location-only filtering
 */
interface MatchmakingStepperModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFindMatches: (answers: { answer_q1: string; answer_q2: string; answer_q3: string }) => void;
  selectedAddress: string;
  setSelectedAddress: (address: string) => void;
  onAddressSelectAndGeocode: (data: GeocodedAddressData) => void;
  addressResetTrigger: number;
  selectedState: string;
  selectedCounty: string;
  selectedZipCode: string;
  selectedLatitude: number | null;
  selectedLongitude: number | null;
  persistedAnswers: { answer_q1: string; answer_q2: string; answer_q3: string };
  setPersistedAnswers: (answers: { answer_q1: string; answer_q2: string; answer_q3: string }) => void;
}

const MatchmakingStepperModal = ({
  isOpen,
  onClose,
  onFindMatches,
  selectedAddress,
  setSelectedAddress,
  onAddressSelectAndGeocode,
  addressResetTrigger,
  selectedState,
  selectedCounty,
  selectedZipCode,
  selectedLatitude,
  selectedLongitude,
  persistedAnswers,
  setPersistedAnswers,
}: MatchmakingStepperModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [question1Answer, setQuestion1Answer] = useState<string>('');
  const [question2Answer, setQuestion2Answer] = useState<string>('');
  const [question3Answer, setQuestion3Answer] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setQuestion1Answer(persistedAnswers.answer_q1 || '');
      setQuestion2Answer(persistedAnswers.answer_q2 || '');
      setQuestion3Answer(persistedAnswers.answer_q3 || '');
      setCurrentStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setPersistedAnswers({
        answer_q1: question1Answer,
        answer_q2: question2Answer,
        answer_q3: question3Answer,
      });
    }
  }, [question1Answer, question2Answer, question3Answer, isOpen, setPersistedAnswers]);

  const currentStepRef = useRef(currentStep);
  
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  const handleNext = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep === 1) {
      setQuestion1Answer('');
    } else if (currentStep === 2) {
      setQuestion2Answer('');
    } else if (currentStep === 3) {
      setQuestion3Answer('');
    }
    setCurrentStep(4);
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFindMatches = () => {
    onFindMatches({
      answer_q1: question1Answer?.trim() || '',
      answer_q2: question2Answer?.trim() || '',
      answer_q3: question3Answer?.trim() || '',
    });
  };

  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
      case 2:
      case 3:
        return true;
      case 4:
        return selectedAddress && selectedLatitude && selectedLongitude;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-sm px-2 sm:px-4"
      style={{ fontFamily: '"Inter", sans-serif' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative w-full max-w-[48rem] rounded-[15px] bg-[#F9FAFB] px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10 shadow-[0_30px_80px_rgba(15,23,42,0.18)] max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 sm:right-6 sm:top-6 text-[#9AA3B7] transition-colors hover:text-[#6f778c] z-50"
          aria-label="Close modal"
        >
          <svg
            width="25"
            height="25"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-semibold text-[#111827] mb-1">
                Help me find the perfect match!
              </h2>
              <p className="text-xs sm:text-sm font-termina text-[#6B7280]">
                We're going to ask a few more questions to ensure that we get the best results.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm font-termina text-[#6B7280]">
                Step {currentStep} of 4
              </span>
            </div>
          </div>

          <div className="w-full flex gap-1.5">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex-1 h-1.5 rounded-full transition-all duration-300 ease-out ${
                  step <= currentStep
                    ? 'bg-[#026857]'
                    : 'bg-[#E5E7EB]'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="mb-6 sm:mb-8 min-h-[300px] sm:min-h-[350px] bg-white rounded-lg p-4 sm:p-6">
          {currentStep === 1 && (
            <QuestionStep
              question={STEP_QUESTIONS[0]}
              answer={question1Answer}
              onAnswerChange={setQuestion1Answer}
            />
          )}

          {currentStep === 2 && (
            <QuestionStep
              question={STEP_QUESTIONS[1]}
              answer={question2Answer}
              onAnswerChange={setQuestion2Answer}
            />
          )}

          {currentStep === 3 && (
            <QuestionStep
              question={STEP_QUESTIONS[2]}
              answer={question3Answer}
              onAnswerChange={setQuestion3Answer}
            />
          )}

          {currentStep === 4 && (
            <LocationStep
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              onAddressSelectAndGeocode={onAddressSelectAndGeocode}
              addressResetTrigger={addressResetTrigger}
              selectedState={selectedState}
              selectedCounty={selectedCounty}
              selectedZipCode={selectedZipCode}
            />
          )}
        </div>

        <NavigationButtons
          currentStep={currentStep}
          canProceed={!!canProceedToNext()}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onSkip={handleSkip}
          onClose={onClose}
          onFindMatches={handleFindMatches}
        />
      </div>
    </div>
  );
};

export default MatchmakingStepperModal;

